import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Coins, Check, AlertCircle, Sparkles } from "lucide-react";
import StrexConfirmFlow from "../components/StrexConfirmFlow";

export default function KjopBekreft() {
  const { purchaseId } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [purchase, setPurchase] = useState(null);

  useEffect(() => {
    api.get(`/wallet/purchase/${purchaseId}`)
      .then((r) => setPurchase(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [purchaseId]);

  const confirmMock = async (simulate) => {
    setBusy(true);
    try {
      const r = await api.post("/wallet/purchase/confirm", {
        purchase_id: purchaseId, simulate
      });
      setResult(r.data);
      if (r.data.ok && refresh) refresh();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const handleStrexSuccess = (data) => {
    setResult(data);
    if (refresh) refresh();
  };
  const handleStrexFailure = (data) => {
    setResult({ ok: false, message: data.message || "Strex avviste betalingen." });
  };

  const isStrex = purchase?.method === "strex";

  return (
    <Layout withSidebar={false}>
      <div className="max-w-xl mx-auto">
        <div className="font-meta mb-3">
          {isStrex ? "Strex mobilbetaling" : "Mock-betaling"}
        </div>
        <h1 className="font-fraunces italic text-4xl text-[var(--cream)] mb-8 leading-tight">
          {isStrex ? "Bekreft kjøp på mobil." : "Bekreft betalingen."}
        </h1>

        {!result && !purchase && (
          <div className="font-meta text-[var(--cream-soft)]">Laster…</div>
        )}

        {!result && purchase && isStrex && (
          <StrexConfirmFlow
            purchaseId={purchaseId}
            amountNok={purchase.final_price}
            onSuccess={handleStrexSuccess}
            onFailure={handleStrexFailure}
          />
        )}

        {!result && purchase && !isStrex && (
          <div className="border border-white/10 p-8" data-testid="mock-confirm-card">
            <div className="border-l-4 border-[var(--gold)] bg-[var(--gold)]/5 p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-4 w-4 text-[var(--gold)] shrink-0 mt-0.5" />
                <p className="font-manrope text-sm text-[var(--cream-soft)] leading-relaxed">
                  <strong className="text-[var(--cream)]">Testperiode:</strong> Dette er en mock-betaling. Ingen ekte penger trekkes.
                  Velg «Bekreft» nedenfor for å simulere vellykket betaling, eller «Avbryt» for å simulere feil.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <button onClick={() => confirmMock("success")} disabled={busy}
                className="btn-magenta w-full inline-flex items-center justify-center gap-2 py-3"
                data-testid="mock-confirm-success">
                <Check className="h-4 w-4" /> Bekreft betaling (suksess)
              </button>
              <button onClick={() => confirmMock("failure")} disabled={busy}
                className="btn-ghost w-full" data-testid="mock-confirm-failure">
                Simulér feil / avbryt
              </button>
            </div>
          </div>
        )}

        {result && result.ok && (
          <div className="border border-emerald-500/30 bg-emerald-500/5 p-8" data-testid="purchase-success">
            <Check className="h-10 w-10 text-emerald-400 mb-4" />
            <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Betaling vellykket!</h2>
            <div className="space-y-2 mb-6">
              <div className="font-manrope text-base text-[var(--cream)] inline-flex items-center gap-2">
                <Coins className="h-4 w-4 text-[var(--gold)]" />
                <span data-testid="tokens-credited">+{result.tokens_credited} tokens</span> kreditert
              </div>
              {result.bonus_tokens > 0 && (
                <div className="font-manrope text-base text-[var(--gold)] inline-flex items-center gap-2" data-testid="bonus-banner">
                  <Sparkles className="h-4 w-4" />
                  +{result.bonus_tokens} VERIFISERINGS-BONUS! Du er nå verifisert.
                </div>
              )}
              <div className="font-manrope text-sm text-[var(--cream-soft)]">
                Ny saldo: <strong className="text-[var(--cream)]">{result.new_balance} tokens</strong>
              </div>
            </div>
            <div className="flex gap-3">
              <Link to="/wallet" className="btn-magenta inline-flex items-center gap-2" data-testid="go-wallet">
                <Coins className="h-4 w-4" /> Til min wallet
              </Link>
              <button onClick={() => navigate("/kjop")} className="btn-ghost">Kjøp flere</button>
            </div>
          </div>
        )}

        {result && !result.ok && (
          <div className="border border-red-500/30 bg-red-500/5 p-8" data-testid="purchase-failed">
            <AlertCircle className="h-10 w-10 text-red-400 mb-4" />
            <h2 className="font-fraunces italic text-3xl text-[var(--cream)] mb-3">Betaling avbrutt</h2>
            <p className="font-manrope text-sm text-[var(--cream-soft)] mb-6">{result.message}</p>
            <button onClick={() => navigate("/kjop")} className="btn-magenta">Prøv igjen</button>
          </div>
        )}
      </div>
    </Layout>
  );
}
